import { SCHEDULE_DAY_VIEWS, SCHEDULE_VIEW_TYPES } from "pages/ScheduleV6/constants/constants";
import { PUBLISH_SHIFT, matchesShift } from "pages/ScheduleV6/constants/shifts";
import { useMemo } from "react";
import { useSelector } from "react-redux";

const emptyData = { workers: [], ids: [], error: null, noClSheets: [], availableShifts: new Set() };

const buildData = (eligibleSheets, shift, workers) => {
  const availableShifts = new Set(
    eligibleSheets.map(sheet => (sheet?.timeOfDay === PUBLISH_SHIFT.NIGHT ? PUBLISH_SHIFT.NIGHT : PUBLISH_SHIFT.DAY))
  );

  const sheetsToPublish = eligibleSheets.filter(sheet => matchesShift(sheet, shift));

  const sheetsWithoutForeman = sheetsToPublish.filter(sheet => sheet.workers?.every(w => !w.foreman));
  if (sheetsWithoutForeman.length) {
    return {
      workers: [],
      ids: [],
      error: { type: 'no-cl', text: 'Crew Leader missing from highlighted project(s).' },
      noClSheets: sheetsWithoutForeman.map(sheet => sheet._id),
      availableShifts,
    };
  }
  if (!sheetsToPublish.length) {
    return {
      workers: [],
      ids: [],
      error: { type: 'no-wl', text: 'No sheets for publish' },
      noClSheets: [],
      availableShifts,
    };
  }

  const workersToPublish = {};
  sheetsToPublish.forEach(sheet => {
    const project = sheet.project;
    const title = project?.route + (project.section ? ', ' + project.section : '');
    sheet.workers.forEach(worker => {
      if (!workersToPublish[worker._id]) {
        workersToPublish[worker._id] = {
          _id: worker._id,
          addedTo: [],
          user: workers?.find(w => w._id === worker._id),
        };
      }
      workersToPublish[worker._id].addedTo.push(title);
    });
  });

  return {
    workers: Object.values(workersToPublish),
    ids: sheetsToPublish.map(sheet => sheet.sheet?._id || sheet._id),
    error: null,
    noClSheets: [],
    availableShifts,
  };
};

const usePublishData = (publishShift = PUBLISH_SHIFT.FULL, updateShift = PUBLISH_SHIFT.FULL) => {
  const {
    scheduleView,
    scheduleType,
    sheets,
    monthSheets,
    weekSheets,
    resources,
  } = useSelector((store) => store.schedule);

  const allSheets = useMemo(() => {
    switch (scheduleView) {
      case SCHEDULE_VIEW_TYPES.monthly:
        return Object.values(monthSheets).flat();
      case SCHEDULE_VIEW_TYPES.weekly:
        return Object.values(weekSheets).flat();
      default:
        switch (scheduleType) {
          case SCHEDULE_DAY_VIEWS.grid:
            return sheets.filter(sheet => Number.isInteger(sheet.grid.position)).map(sheet => (sheet?.sheet ? sheet.sheet : sheet))
          default:
            return sheets.map(sheet => (sheet?.sheet ? sheet.sheet : sheet));
        }
    }
  }, [sheets, monthSheets, weekSheets, scheduleView, scheduleType]);

  const publishEligible = useMemo(
    () => allSheets.filter(sheet => sheet.workers?.length && !sheet?.published),
    [allSheets]
  );

  const updateEligible = useMemo(
    () => allSheets.filter(sheet => sheet.workers?.length && sheet?.published && sheet?.unpublishedChanges),
    [allSheets]
  );

  const publish = useMemo(
    () => (publishEligible.length ? buildData(publishEligible, publishShift, resources?.workers) : emptyData),
    [publishEligible, publishShift, resources?.workers]
  );

  const update = useMemo(
    () => (updateEligible.length ? buildData(updateEligible, updateShift, resources?.workers) : emptyData),
    [updateEligible, updateShift, resources?.workers]
  );

  return { publish, update };
};

export default usePublishData;
